import { useQuery } from "@apollo/client";
import { Box, Button, Stack } from "@mui/material";
import { RecoverStrandedMembersDialog } from "components/Cohort/RecoverStrandedMembersDialog";
import { ReopenCohortDialog } from "components/Cohort/ReopenCohortDialog";
import { gql } from "generated-graphql";
import {
  ActivityType,
  CohortStatus,
  GetTierIiReportQuery,
  WorkflowStatus,
} from "generated-graphql/graphql";
import { useResetSafetyOverrideAuth } from "hooks/useResetReportAuth";
import { useMemo, useState } from "react";

const GET_SOURCE_COHORT_STATUS = gql(`
  query GetSourceCohortStatus($cohortId: ID!) {
    cohort(id: $cohortId) {
      id
      status
      allowsMultipleCohorts
      currentWorkflow {
        id
        status
        currentActivity {
          id
          type
        }
      }
    }
  }
`);

type LateArrivalRecoveryActionsProps = {
  errorMessage: string;
  cohortMemberships: GetTierIiReportQuery["tierIIReport"]["cohortMemberships"];
};

function extractCohortIdsFromLateArrivalError(message: string): string[] {
  const closedCohortIdsSection = message.match(/Closed cohort IDs:\s*(.+)$/i);
  if (!closedCohortIdsSection?.[1]) {
    return [];
  }
  return closedCohortIdsSection[1]
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

export function LateArrivalRecoveryActions({
  errorMessage,
  cohortMemberships,
}: LateArrivalRecoveryActionsProps) {
  const [recoverDialogOpen, setRecoverDialogOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const { canOverrideSafetyCheck } = useResetSafetyOverrideAuth();

  const parsedSourceCohortIds = useMemo(
    () => extractCohortIdsFromLateArrivalError(errorMessage),
    [errorMessage]
  );

  const sourceCohortId = useMemo(() => {
    if (parsedSourceCohortIds.length > 0) {
      return parsedSourceCohortIds[0];
    }

    const closedMembership = cohortMemberships.find(
      (membership) => membership.cohort.status === CohortStatus.Closed
    );
    return closedMembership?.cohort.id;
  }, [cohortMemberships, parsedSourceCohortIds]);

  const sourceCohortMembership = useMemo(() => {
    if (!sourceCohortId) {
      return null;
    }
    return (
      cohortMemberships.find(
        (membership) => membership.cohort.id === sourceCohortId
      ) ?? null
    );
  }, [cohortMemberships, sourceCohortId]);

  // The report's cohortMemberships may not include the source cohort if its
  // CohortMember status is NOT_PARTICIPATING (filtered out by the query).
  // In that case, fetch the cohort directly to determine button eligibility.
  const needsFallbackQuery =
    parsedSourceCohortIds.length > 0 && !sourceCohortMembership;

  const { data: fallbackData } = useQuery(GET_SOURCE_COHORT_STATUS, {
    variables: { cohortId: sourceCohortId ?? "" },
    skip: !needsFallbackQuery || !sourceCohortId,
  });

  const sourceCohort = sourceCohortMembership?.cohort ?? fallbackData?.cohort;

  const canRecoverFromSourceCohort =
    sourceCohort != null &&
    (parsedSourceCohortIds.length > 0 ||
      (sourceCohort.status === CohortStatus.Closed &&
        sourceCohort.allowsMultipleCohorts === false)) &&
    sourceCohort.currentWorkflow?.status === WorkflowStatus.Completed;

  const canReopenSourceCohort =
    sourceCohort != null &&
    sourceCohort.status === CohortStatus.Closed &&
    sourceCohort.allowsMultipleCohorts === false &&
    (sourceCohort.currentWorkflow?.status === WorkflowStatus.Error ||
      sourceCohort.currentWorkflow?.currentActivity?.type ===
        ActivityType.Staff);

  if (
    !sourceCohortId ||
    (!canRecoverFromSourceCohort && !canReopenSourceCohort) ||
    !canOverrideSafetyCheck
  ) {
    return null;
  }

  return (
    <>
      <Box sx={{ mt: 1 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const cohortUrl = `${window.location.origin}/staff/cohorts/${sourceCohortId}/activities`;
              const newTab = window.open(cohortUrl, "_blank");
              if (newTab) {
                newTab.focus();
              }
            }}
          >
            View Closed Cohort
          </Button>
          {canRecoverFromSourceCohort && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setRecoverDialogOpen(true)}
            >
              Create Recovery Cohort
            </Button>
          )}
          {canReopenSourceCohort && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setReopenDialogOpen(true)}
            >
              Reopen Cohort
            </Button>
          )}
        </Stack>
      </Box>
      <RecoverStrandedMembersDialog
        open={recoverDialogOpen}
        onClose={() => setRecoverDialogOpen(false)}
        sourceCohortId={sourceCohortId}
        canExecuteRecovery={canOverrideSafetyCheck}
        onSuccess={(newCohortId) => {
          const newTab = window.open(
            `${window.location.origin}/staff/cohorts/${newCohortId}`,
            "_blank"
          );
          if (newTab) {
            newTab.focus();
          }
        }}
      />
      <ReopenCohortDialog
        open={reopenDialogOpen}
        onClose={() => setReopenDialogOpen(false)}
        sourceCohortId={sourceCohortId}
        canExecuteReopen={canOverrideSafetyCheck}
      />
    </>
  );
}
